import { useState, useRef, useCallback, useEffect } from 'react';
import { systemUpdatesApi, SystemUpdate } from '../../../domains/admin/api/systemUpdates';
import { changeTrackingApi } from '../../../services/changeTracking.api';
import { AIMessage } from '../components/types';
import { onAIEvent, dispatchAIEvent } from '../utils/events';
import { getShownUpdateIds, markUpdatesAsShown } from '../utils/localStorage';

const getCategoryIcon = (category: string): string => {
  const icons: Record<string, string> = {
    ai_capability: '🧠',
    feature: '✨',
    bugfix: '🐛',
    security: '🛡️',
    performance: '⚡',
    maintenance: '🔧',
    announcement: '📢'
  };
  return icons[category] || '📋';
};

interface UseSystemUpdatesParams {
  userId: number | undefined;
  isOpen: boolean;
  canAdminSystem: boolean;
  canReviewCompliance: boolean;
  setMessages: React.Dispatch<React.SetStateAction<AIMessage[]>>;
}

interface UseSystemUpdatesReturn {
  hasUnseenUpdates: boolean;
  hasUnseenChangeNotifications: boolean;
}

export function useSystemUpdates({
  userId,
  isOpen,
  canAdminSystem,
  canReviewCompliance,
  setMessages,
}: UseSystemUpdatesParams): UseSystemUpdatesReturn {
  const [hasUnseenUpdates, setHasUnseenUpdates] = useState(false);
  const [pendingUpdates, setPendingUpdates] = useState<SystemUpdate[]>([]);
  const [hasUnseenChangeNotifications, setHasUnseenChangeNotifications] = useState(false);
  const [pendingChangeNotifications, setPendingChangeNotifications] = useState<any[]>([]);
  const hasDeliveredUpdates = useRef(false);
  const hasDeliveredChangeNotifications = useRef(false);

  const groupNotificationsByAggregation = (notifications: any[]) => {
    const groups: any[] = [];
    const processedIds = new Set();

    for (const notification of notifications) {
      if (processedIds.has(notification.id)) continue;

      const aggregationGroup = notifications.filter(n =>
        n.aggregation_id === notification.aggregation_id && !processedIds.has(n.id)
      );

      if (aggregationGroup.length > 0) {
        aggregationGroup.forEach(n => processedIds.add(n.id));
        groups.push({
          type: 'aggregation',
          aggregation: aggregationGroup[0].aggregation,
          notifications: aggregationGroup
        });
      }
    }

    for (const notification of notifications) {
      if (!processedIds.has(notification.id)) {
        groups.push({
          type: 'individual',
          notifications: [notification]
        });
        processedIds.add(notification.id);
      }
    }

    return groups;
  };

  const buildChangeNotificationMessage = (group: any): string => {
    if (group.type === 'aggregation' && group.aggregation) {
      const agg = group.aggregation;
      return `Hey! 👋 I noticed some changes in the system: **${agg.title}**\n\n${agg.description || agg.title}\n\nThis affects ${group.notifications.length} item(s). Let me know if you'd like more details!`;
    } else {
      const notification = group.notifications[0];
      const entityType = notification.entity_type || 'item';
      const changeType = notification.change_type || 'modified';
      return `Hi there! 👋 Just letting you know that a ${entityType} was ${changeType} in the system. This might be relevant to your work - feel free to ask me about it!`;
    }
  };

  const buildRoleAwareUpdateMessage = useCallback((updates: SystemUpdate[]): string => {
    const updateCount = updates.length;
    const updateWord = updateCount === 1 ? 'update' : 'updates';

    if (canAdminSystem) {
      let msg = `Hey! 👋 I've got some exciting news — there ${updateCount === 1 ? 'is' : 'are'} **${updateCount} new system ${updateWord}** you should know about:\n\n`;
      updates.forEach((update, i) => {
        const icon = getCategoryIcon(update.category);
        msg += `${icon} **${update.title}**`;
        if (update.version) msg += ` *(v${update.version})*`;
        msg += `\n${update.summary}`;
        if (update.details) msg += `\n\n${update.details}`;
        if (i < updates.length - 1) msg += '\n\n---\n\n';
      });
      msg += '\n\nFeel free to ask me about any of these changes or try out the new features!';
      return msg;
    }

    if (canReviewCompliance) {
      let msg = `Hi there! 👋 I wanted to let you know that I've been **upgraded** with some new capabilities:\n\n`;
      updates.forEach((update) => {
        const icon = getCategoryIcon(update.category);
        msg += `${icon} **${update.title}** — ${update.summary}\n\n`;
      });
      msg += `These improvements mean I can now help you even more effectively with your compliance tasks. Just ask me anything!`;
      return msg;
    }

    let msg = `Hi! 👋 Just a quick heads up — I've recently been improved:\n\n`;
    updates.forEach((update) => {
      msg += `• **${update.title}** — ${update.summary}\n`;
    });
    msg += `\nFeel free to try these out by asking me anything!`;
    return msg;
  }, [canAdminSystem, canReviewCompliance]);

  const deliverUpdateMessage = useCallback(async (updates: SystemUpdate[]) => {
    const updateMessage = buildRoleAwareUpdateMessage(updates);

    const updateMsg: AIMessage = {
      id: `system_update_${Date.now()}`,
      role: 'assistant',
      content: updateMessage,
      timestamp: new Date(),
      meta: { isSystemUpdate: true },
    };

    setMessages(prev => [...prev, updateMsg]);
    hasDeliveredUpdates.current = true;
    setHasUnseenUpdates(false);
    markUpdatesAsShown(updates.map(u => u.id));

    try {
      await systemUpdatesApi.dismissAllUpdates();
      dispatchAIEvent('systemUpdatesDismissed');
    } catch { /* silently fail */ }
    setPendingUpdates([]);
  }, [buildRoleAwareUpdateMessage, setMessages]);

  const deliverChangeNotifications = useCallback(async (notifications: any[]) => {
    if (notifications.length === 0) return;

    const groupedNotifications = groupNotificationsByAggregation(notifications);

    for (const group of groupedNotifications) {
      const notificationMessage = buildChangeNotificationMessage(group);

      const notificationMsg: AIMessage = {
        id: `change_notification_${Date.now()}_${Math.random()}`,
        role: 'assistant',
        content: notificationMessage,
        timestamp: new Date(),
        meta: { isChangeNotification: true, notificationGroup: group },
      };

      setMessages(prev => [...prev, notificationMsg]);
    }

    hasDeliveredChangeNotifications.current = true;
    setHasUnseenChangeNotifications(false);
    setPendingChangeNotifications([]);
  }, [setMessages]);

  // Check for unseen system updates and change notifications on mount
  useEffect(() => {
    if (!userId) return;
    const timer = setTimeout(async () => {
      try {
        const countResponse = await systemUpdatesApi.getUnseenCount();
        if (countResponse > 0) {
          setHasUnseenUpdates(true);
          const updatesResponse = await systemUpdatesApi.getUnseenUpdates();
          if (updatesResponse.success && updatesResponse.updates.length > 0) {
            const shownIds = getShownUpdateIds();
            const newUpdates = updatesResponse.updates.filter(u => !shownIds.includes(u.id));
            if (newUpdates.length > 0) {
              setPendingUpdates(newUpdates);
            }
          }
        }

        const changeNotifications = await changeTrackingApi.getUserRelevantChanges(50, 0);
        if (changeNotifications.changes && changeNotifications.changes.length > 0) {
          const unseenChanges = changeNotifications.changes.filter((change: any) => !change.is_read);
          if (unseenChanges.length > 0) {
            setHasUnseenChangeNotifications(true);
            setPendingChangeNotifications(unseenChanges);
          }
        }
      } catch { /* silently fail */ }
    }, 1500);
    return () => clearTimeout(timer);
  }, [userId]);

  // Auto-deliver update message when chat opens
  useEffect(() => {
    if (isOpen && pendingUpdates.length > 0 && !hasDeliveredUpdates.current) {
      deliverUpdateMessage(pendingUpdates);
    }
  }, [isOpen, pendingUpdates, deliverUpdateMessage]);

  // Auto-deliver change notifications when chat opens
  useEffect(() => {
    if (isOpen && pendingChangeNotifications.length > 0 && !hasDeliveredChangeNotifications.current) {
      deliverChangeNotifications(pendingChangeNotifications);
    }
  }, [isOpen, pendingChangeNotifications, deliverChangeNotifications]);

  // Listen for real-time change notifications via typed event
  useEffect(() => {
    return onAIEvent('changeNotificationReceived', (notification) => {
      if (!isOpen) {
        setHasUnseenChangeNotifications(true);
        setPendingChangeNotifications(prev => [...prev, notification]);
      } else {
        deliverChangeNotifications([notification]);
      }
    });
  }, [isOpen, deliverChangeNotifications]);

  return {
    hasUnseenUpdates,
    hasUnseenChangeNotifications,
  };
}
